import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, countLabel, formatDateString, PLATFORM_DATE_FORMAT } from '@shapeable/utils';
import { Topic } from '@shapeable/gesda-types';
import { CellLabel, EntityBannerProps, EntityLink, EntityMetaSection, EntityValue, useLang, useMonthNames } from '@shapeable/ui';
import { useCurrentEditionChairs } from '../../hooks/use-current-edition-chairs';
import { TopicActionBar } from './topic-action-bar';
import { TopicBannerMedia } from './topic-banner-media';
import { useCurrentEditionMembers } from '../../hooks/use-current-edition-members';
import { doiUrlFor, normalizeDoiId } from '../../utils/citation';
import { TruncatedExternalLink } from '../elements/truncated-external-link';
const cls = classNames('topic-banner-layout');

// -------- Types -------->

// -------- Props -------->

export type TopicBannerLayoutProps = Classable & HasChildren & EntityBannerProps & { 
  entity?: Topic;
};

export const TopicBannerLayoutDefaultProps: Omit<TopicBannerLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {
  color?: string;
};

type ColorProps = {
  color?: string;
};

type SparseBannerProps = {
  $isMediaExpanded?: boolean;
  $sparseBanner?: boolean;
  $bubbleSize?: number;
};

type MainStyleProps = SparseBannerProps;

type HeaderStyleProps = {
  $isMediaExpanded?: boolean;
  $bubbleSize?: number;
};

type ContentStyleProps = SparseBannerProps;

/** Collapsed bubble footprint — keep in sync with topic-banner-media top offsets (10 / 40) */
const sparseBannerMinHeightPx = (bubbleSize = 90) => {
  const collapsedTop = bubbleSize > 90 ? 40 : 10;
  return bubbleSize + collapsedTop + 16;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    font-weight: 300;

    ${({ color }: ContainerProps) => color && css`
      border-top: 4px solid ${color || theme.COLOR('primary')};
    `}
  `,
  desktop: css`
    gap: ${theme.UNIT(4)};
  `,
});

const MainStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    /* Only reserve a full right lane when the large bubble is in play */
    padding-right: ${({ $isMediaExpanded, $bubbleSize }: MainStyleProps) => {
      if ($isMediaExpanded) {
        return 0;
      }
      const size = $bubbleSize || 90;
      return size > 90 ? size + 24 : 0;
    }}px;
    ${({ $isMediaExpanded, $sparseBanner, $bubbleSize }: MainStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        min-height: ${sparseBannerMinHeightPx($bubbleSize)}px;
        justify-content: flex-end;
      `
    )};
    transition: margin-top 0.25s ease, padding-right 0.2s ease, min-height 0.2s ease;
  `,
  landscape: css`
    ${({ $isMediaExpanded, $sparseBanner, $bubbleSize }: MainStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        min-height: ${sparseBannerMinHeightPx($bubbleSize)}px;
        justify-content: flex-end;
      `
    )};
  `,
  tablet: css`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.UNIT(3)};
    margin-top: ${({ $isMediaExpanded }: MainStyleProps) => (
      $isMediaExpanded ? 0 : theme.UNIT(4)
    )};
    ${({ $isMediaExpanded, $sparseBanner, $bubbleSize }: MainStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        min-height: ${sparseBannerMinHeightPx($bubbleSize)}px;
        align-items: flex-end;
      `
    )};
  `,
  tabletDevice: css`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.UNIT(3)};
    ${({ $isMediaExpanded, $sparseBanner, $bubbleSize }: MainStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        min-height: ${sparseBannerMinHeightPx($bubbleSize)}px;
        align-items: flex-end;
      `
    )};
  `,
  desktopDevice: css`
    
  `,
});

const ContentStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(4)};
    flex: 1 1 auto;
    min-width: 0;
    padding: ${theme.UNIT(4)} ${theme.UNIT(4)} 0;
    padding-top: ${({ $isMediaExpanded }: ContentStyleProps) => (
      $isMediaExpanded ? 0 : theme.UNIT(4)
    )};
    ${({ $isMediaExpanded, $sparseBanner }: ContentStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        justify-content: flex-end;
      `
    )};
    transition: padding-top 0.25s ease;
  `,
  landscape: css`
    ${({ $isMediaExpanded, $sparseBanner }: ContentStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        justify-content: flex-end;
      `
    )};
  `,
  tablet: css`
    padding: ${theme.UNIT(7)} ${theme.UNIT(4)} 0;
    padding-top: ${({ $isMediaExpanded }: ContentStyleProps) => (
      $isMediaExpanded ? 0 : theme.UNIT(7)
    )};
    ${({ $isMediaExpanded, $sparseBanner }: ContentStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        justify-content: flex-end;
      `
    )};
  `,
  desktopDevice: css`
    padding: ${theme.UNIT(10)} 0 0;
    padding-top: ${({ $isMediaExpanded }: ContentStyleProps) => (
      $isMediaExpanded ? 0 : theme.UNIT(10)
    )};
    ${({ $isMediaExpanded, $sparseBanner }: ContentStyleProps) => (
      !$isMediaExpanded && $sparseBanner && css`
        justify-content: flex-end;
      `
    )};
  `,
});

const TopicHeaderStyles = breakpoints({
  base: css`
    /* Compact bubble: clear only the title so body copy can use the full width */
    padding-right: ${({ $isMediaExpanded, $bubbleSize }: HeaderStyleProps) => {
      if ($isMediaExpanded) {
        return 0;
      }
      const size = $bubbleSize || 90;
      return size > 90 ? 0 : size + 12;
    }}px;
    transition: padding-right 0.2s ease;

    .shp--cell-header {
      color: ${theme.COLOR('dark')};
    }
  `,
});

const ContributorsStyles = breakpoints({
  base: css`
    
  `,
});

const ContributorListStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: ${theme.UNIT(1)};
    font-size: 1em;
    font-weight: 400;
    color: ${theme.COLOR('text-mid')};
    line-height: 1.5;
  `,
});

const ContributorLinkStyles = breakpoints({
  base: css`
    display: inline-grid;
    text-decoration: underline;
    text-underline-offset: 0.02em;
    text-underline-position: from-font;
    text-decoration-thickness: 0.8px;
    color: ${theme.COLOR('mid')};
    ${theme.TRANSITION('color')};

    &:hover {
      color: ${({ color }: ColorProps ) => color || theme.COLOR('primary')};
      text-decoration-thickness: 1.5px;
      
      ${cls.child('contributor-label')} {
        font-weight: 400;
      }
    }
  `,
});

const ContributorLabelStyles = breakpoints({
  base: css`
    grid-area: 1 / 1;
    font-weight: 300;
  `,
});

const ContributorSizerStyles = breakpoints({
  base: css`
    grid-area: 1 / 1;
    font-weight: 400;
    visibility: hidden;
    pointer-events: none;
    user-select: none;
  `,
});

const ContributorSuffixStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('mid')};
    font-weight: 300;
    text-decoration: underline;
  `,
});

const CitationMetaStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: 1em;
    color: ${theme.COLOR('text-mid')};
  `,
  
});

const PublisherStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
    padding-right: ${theme.UNIT(2)};
  `,
  
});

const DateStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
    padding-right: ${theme.UNIT(2)};
  `,
  
});

const DoiStyles = breakpoints({
  base: css`
    display: flex;
    align-items: center;
    color: ${theme.COLOR('text')};
  `,
});

const DoiLinkStyles = breakpoints({
  base: css`
  `,
});

const ModeratorsStyles = breakpoints({
  base: css`
  `,
});

const ModeratorTitleStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-weight: 300;
    margin-bottom: ${theme.UNIT(2)};
    font-size: 0.9em;
  `,
});

const ModeratorValuesStyles = breakpoints({
  base: css`
    font-size: ${10 / 16}em;
    padding: 0;
    .shp--entity-value__body {
      align-items: flex-start;
      padding-right: ${theme.UNIT(2)};
    }
  `,
  tablet: css`
    display: flex;
  `,
});

const ActionBarStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(3)};
  `,
});

const MediaStyles = breakpoints({
  base: css`
    
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div`${ContainerStyles}`,
    Media: styled(TopicBannerMedia)`${MediaStyles}`,
    Main: styled.div.attrs(cls.attr('main'))<MainStyleProps>`${MainStyles}`,
      Content: styled.div.attrs(cls.attr('content'))<ContentStyleProps>`${ContentStyles}`,
        TopicHeader: styled(EntityValue)<HeaderStyleProps>`${TopicHeaderStyles}`,
        Contributors: styled.div.attrs(cls.attr('contributors'))`${ContributorsStyles}`,
          ContributorList: styled.div.attrs(cls.attr('contributor-list'))`${ContributorListStyles}`,
            ContributorLink: styled(EntityLink).attrs(cls.attr('contributor-link'))<ColorProps>`${ContributorLinkStyles}`,
              ContributorLabel: styled.span.attrs(cls.attr('contributor-label'))`${ContributorLabelStyles}`,
              ContributorSizer: styled.span.attrs(cls.attr('contributor-sizer'))`${ContributorSizerStyles}`,
            ContributorSuffix: styled.span.attrs(cls.attr('contributor-suffix'))`${ContributorSuffixStyles}`,
        CitationMeta: styled.div`${CitationMetaStyles}`,
          Publisher: styled.span`${PublisherStyles}`,
          Date: styled.span`${DateStyles}`,
          Doi: styled.span`${DoiStyles}`,
            DoiLink: styled.span`${DoiLinkStyles}`,
        Moderators: styled.div`${ModeratorsStyles}`,
        ContributorTitle: styled(CellLabel)`${ModeratorTitleStyles}`,
          ModeratorTitle: styled(CellLabel)`${ModeratorTitleStyles}`,
            ModeratorValues: styled(EntityMetaSection)`${ModeratorValuesStyles}`, 
    ActionBar: styled(TopicActionBar)`${ActionBarStyles}`,
};

export const TopicBannerLayout: Shapeable.FC<TopicBannerLayoutProps> = (props) => {
  const { className, children, entity } = props;

  const { committeeChairs = [], committeeMembers = [], color, doiId: rawDoiId, published } = entity;
  const doiId = normalizeDoiId(rawDoiId);
  const doiUrl = doiUrlFor(doiId);

  const currentCommitteeMembers = useCurrentEditionMembers(committeeMembers, entity);
  const currentEditionChairs = useCurrentEditionChairs(committeeChairs, entity);
  const t = useLang();
  const monthNames = useMonthNames();
  const [isMediaExpanded, setIsMediaExpanded] = useState(false);
  const [bubbleSize, setBubbleSize] = useState(90);

  const hasContributors = !!currentCommitteeMembers.length;
  const hasChairs = !!currentEditionChairs.length;
  const hasDoi = !!doiId;
  const sparseBanner = !isMediaExpanded && !hasChairs && !hasContributors;

  const formattedDate = published
    ? formatDateString(published.slice(0, 10), t('D MMMM YYYY'), PLATFORM_DATE_FORMAT, { monthNames })
    : '';

  const chairTitle = countLabel(currentEditionChairs.length, {
    one: t('Anticipation Committee Chair'),
    n: t('Anticipation Committee Chair'),
  });

  return (
    <My.Container className={cls.name(className)} color={color?.value}>
      <My.Media
        entity={entity}
        isExpanded={isMediaExpanded}
        onExpand={() => setIsMediaExpanded(true)}
        onCollapse={() => setIsMediaExpanded(false)}
        onBubbleSizeChange={setBubbleSize}
      />

      <My.Main
        $isMediaExpanded={isMediaExpanded}
        $bubbleSize={bubbleSize}
        $sparseBanner={sparseBanner}
      >
        <My.Content $isMediaExpanded={isMediaExpanded} $sparseBanner={sparseBanner}>
          <My.TopicHeader
            entity={entity}
            variant='bannerLarge'
            showImage={false}
            showOutlineNumber={false}
            $isMediaExpanded={isMediaExpanded}
            $bubbleSize={bubbleSize}
          />
          {
            hasChairs && (
              <My.Moderators>
                <My.ModeratorTitle>{chairTitle}:</My.ModeratorTitle>
                <My.ModeratorValues 
                  items={currentEditionChairs} 
                  showAllItems={false} 
                  direction='row' 
                />
              </My.Moderators>
            )
          }
          {
            hasContributors && (
              <My.Contributors>
                <My.ContributorTitle>{t('Anticipation Committee')}:</My.ContributorTitle>
                <My.ContributorList>
                  {
                    currentCommitteeMembers.map((member, index) => {
                      const label = `${member.name}${index < currentCommitteeMembers.length - 1 ? ',' : ''}`;

                      return (
                        <My.ContributorLink key={member.id || member.slug || index} entity={member} color={color?.value}>
                          <My.ContributorLabel>{label}</My.ContributorLabel>
                          <My.ContributorSizer aria-hidden>{label}</My.ContributorSizer>
                        </My.ContributorLink>
                      );
                    })
                  }
                </My.ContributorList>
              </My.Contributors>
            )
          }
          {
            hasDoi && (
              <My.CitationMeta>
                <My.Publisher>{t('GESDA')}</My.Publisher>
                {
                  !!formattedDate &&
                  <My.Date>{formattedDate}</My.Date>
                }
                {
                  <My.Doi>
                    {t('DOI:')}{' '}
                    <My.DoiLink>
                      <TruncatedExternalLink href={doiUrl} label={doiId} />
                    </My.DoiLink>
                  </My.Doi>
                }
              </My.CitationMeta>
            )
          }
        </My.Content>
      </My.Main>

      <My.ActionBar entity={entity} />
      {children}
    </My.Container>
  )
};

TopicBannerLayout.defaultProps = TopicBannerLayoutDefaultProps;
TopicBannerLayout.cls = cls;
